import { Box, Button, Divider, Grid, styled, Tooltip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */

/** UTILS || SLICE */
import { editUser, selectLoading, selectLoadingProfile, selectUserSetup } from 'app/slice/user.slice'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import IconClinicDisabeld from 'assets/icon/icon-clinic-disabeld'
import { ButtonInfoSimple } from 'component/Button/ButtonInfo'
import InputPasswordField from 'component/Input/InputPasswordField'
import Loading from 'component/Loading'
import { colors } from 'constants/theme'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import RadioButton from 'new-components/buttons/radio-button'
import TextInput from 'new-components/inputs/text-input'
import { getPackage } from 'utils/app.utils'

const ButtonPermission = styled(Button)<{ open: boolean }>(({ theme, open }) => ({
  '&.MuiButton-root': {
    height: '2.5rem',
    fontSize: '16px !important',
    paddingLeft: '1rem',
    border: `1px solid ${colors.themeSecondColor} !important`,
    backgroundColor: `${colors.white} !important`,
    color: `${colors.themeSecondColor} !important`,
    minWidth: '150px'
  },
  '.MuiButton-endIcon': {
    transform: open ? 'rotate(180deg);' : ''
  },
  '&.MuiButton-text': {
    color: `${colors.themeSecondColor} !important`
  },
  '&.MuiButton-outlined': {
    color: `${colors.themeSecondColor} !important`,
    border: `1px solid ${colors.themeSecondColor} !important`
  },
  '&.MuiButton-contained': {
    background: `${colors.themeSecondColor} !important`,
    backgroundColor: `${colors.themeSecondColor} !important`,
    color: `${colors.white} !important`,
    padding: '10px 16px !important',
    border: 'none !important',
    '&:hover': {
      backgroundColor: `${colors.themeSecondColor} !important`,
      color: `${colors.white} !important`
    },
    '& .MuiButton-startIcon': {
      marginLeft: 0
    }
  },
  '&.btn-style': {
    borderRadius: '4px'
  }
}))

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  border: 'none',
  '&:first-of-type': {
    marginTop: '1rem !important'
  },
  '&:before': {
    opacity: 0
  },
  '.header-text': {
    h5: {
      fontSize: 18,
      fontWeight: 500,
      '&.required::after': {
        color: colors.statusInactive
      }
    }
  },
  [theme.breakpoints.between('sm', 'lg')]: {
    padding: '0 1.5rem',
    '&:first-of-type': {
      marginTop: '1.5rem !important'
    }
  }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ backgroundColor: colors.white, border: `0.5px solid ${colors.textLightGray}`, borderRadius: '50px', color: colors.themeMainColor }} />} {...props} />
))(({ theme }) => ({
  minHeight: '35px !important',
  backgroundColor: colors.themeSecondColor10,
  '&.error': {
    backgroundColor: colors.statusInactive10
  },
  '.MuiAccordionSummary-content': {
    margin: 0
  }
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 0
  },
  '.MuiGrid-container': {
    margin: 0,
    width: '100%',
    '.MuiGrid-item': {
      width: '100%'
    }
  },
  '.MuiIconButton-root': {
    '&.Mui-disabled': {
      path: {
        fill: colors.disabledGray
      }
    }
  },
  '.btn-parent-remove': {
    paddingTop: '0.875rem',
    [theme.breakpoints.down('xs')]: {
      paddingTop: '0.5rem'
    }
  }
}))

interface SetupAccountProps {
  isEdit: boolean
  errorMessage?: any
  resetErrorMessage?: (type: string) => void
}
export default function SetupAccount(props: SetupAccountProps) {

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const userSetup = useSelector(selectUserSetup)
  const loading = useSelector(selectLoading)
  const loadingProfile = useSelector(selectLoadingProfile)

  const [userCode, setUserCode] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPass, setConfirmPass] = useState('')

  const [scheduleDentist, setScheduleDentist] = useState('0')
  const [scheduleNormal, setScheduleNomal] = useState('0')

  const [hasCancelBill, setHasCancelBill] = useState(false)
  const [hasChangeTreatmentCost, setHasChangeTreatmentCost] = useState('0')
  const [hasEditCourse, setHasEditCourse] = useState(false)
  const [hasChangeTreatmentNote, setHasChangeTreatmentNote] = useState(false)
  const [hasTreatment, setHasTreatment] = useState(false)
  const [hasChangePrice, setHasChangePrice] = useState(false)
  const [hasChangeDiscountAdditional, setHasChangeDiscountAdditional] = useState(false)
  const [manageTreatmentCostType, setManageTreatmentCostType] = useState('')
  const [hasManageAdvances, setHasManageAdvances] = useState(false)
  const [hasUnfollow, setHasUnfollow] = useState(false)
  const [hasTreatmentPlan, setHasTreatmentPlan] = useState(false)
  const [hasTreatmentCourse, setHasTreatmentCourse] = useState(false)
  const [hasDoctorNote, setHasDoctorNote] = useState(false)
  const [hasCancelVisit, setHasCancelVisit] = useState(false)
  const [hasCancelLabOrder, setHasCancelLabOrder] = useState(false)
  const [hasReplyLabOrder, setHasReplyLabOrder] = useState(false)
  const [hasViewIncome, setHasViewIncome] = useState(false)
  const [isViewDfAndLab, setIsViewDfAndLab] = useState(false)
  const [isViewDfConfirm, setIsViewDfConfirm] = useState(false)
  const [autoShowPatientProfile, setAutoShowPatientProfile] = useState(false)
  const [readOnly, setReadOnly] = useState(true)

  // Create date 8/6/2023
  const [hasTreatmentAdd, setHasTreatmentAdd] = useState(false)
  const [hasTreatmentDelete, setHasTreatmentDelete] = useState(false)
  const [hasTreatmentPlanAdd, setHasTreatmentPlanAdd] = useState(false)
  const [hasTreatmentPlanDelete, setHasTreatmentPlanDelete] = useState(false)
  const [hasTreatmentCourseAdd, setHasTreatmentCourseAdd] = useState(false)
  const [hasTreatmentCourseDelete, setHasTreatmentCourseDelete] = useState(false)
  const [hasDoctorNoteAdd, setHasDoctorNoteAdd] = useState(false)
  const [hasDoctorNoteDelete, setHasDoctorNoteDelete] = useState(false)
  const [hasSelectAll, setHasSelectAll] = useState(false)

  const [hasCancelTreatmentQueue, setHasCancelTreatmentQueue] = useState<boolean>(false)
  const [hasInsuranceCost, setHasInsuranceCost] = useState<boolean>(false)
  const [hasEditBill, setHasEditBill] = useState<boolean>(false)
  const [hasResetBill, setHasResetBill] = useState<boolean>(false)
  const [hasShowPhoneNumber, setHasShowPhoneNumber] = useState<boolean>(false)

  const [hasDiagnosis, setHasDiagnosis] = useState<boolean>(false)
  const [hasCreateDiagnosis, setHasCreateDiagnosis] = useState<boolean>(false)
  const [hasDeleteDiagnosis, setHasDeleteDiagnosis] = useState<boolean>(false)

  const [hasMed, setHasMed] = useState<boolean>(false)
  const [hasCreateMed, setHasCreateMed] = useState<boolean>(false)
  const [hasDeleteMed, setHasDeleteMed] = useState<boolean>(false)

  const [hasProduct, setHasProduct] = useState<boolean>(false)
  const [hasCreateProduct, setHasCreateProduct] = useState<boolean>(false)
  const [hasDeleteProduct, setHasDeleteProduct] = useState<boolean>(false)

  const [hasApproveIncomeAndExpenses, setHasApproveIncomeAndExpenses] = useState<boolean>(false)
  const [hasApproveTheAmountChange, setHasApproveTheAmountChange] = useState<boolean>(false)
  const [hasOverdueRequest, setHasOverdueRequest] = useState<boolean>(false)

  const isPackage = getPackage()
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setUserCode(userSetup.userCode)
    setUsername(userSetup.username)
    setPassword(userSetup.password)
    setConfirmPass(userSetup.confirmPass)
    setScheduleNomal(userSetup.ownAppointment)
    setScheduleDentist(userSetup.ownSchedule)
    setHasCancelBill(userSetup.hasCancelBill === 'PERMISS' ? true : false)
    setHasEditCourse(userSetup.hasEditCourse === 'PERMISS' ? true : false)
    setHasChangeTreatmentNote(userSetup.hasChangeTreatmentNote === 'PERMISS' ? true : false)
    setHasTreatment(userSetup.hasTreatment === 'PERMISS' ? true : false)
    setHasChangePrice(userSetup.hasChangePrice === 'PERMISS' ? true : false)
    setHasChangeDiscountAdditional(userSetup.hasChangeDiscountAdditional === 'PERMISS')
    const isTreatmentCostPermiss = userSetup.hasChangeTreatmentCost === 'PERMISS'
    if (isTreatmentCostPermiss) {
      setHasChangeTreatmentCost('1')
      setManageTreatmentCostType(userSetup.manageTreatmentCostType || 'DEFAULT')
    } else {
      setHasChangeTreatmentCost('0')
      setManageTreatmentCostType('')
    }
    setHasManageAdvances(userSetup.hasManageAdvances === 'PERMISS' ? true : false)
    setHasUnfollow(userSetup.hasUnfollow === 'PERMISS' ? true : false)
    setHasTreatmentPlan(userSetup.hasTreatmentPlan === 'PERMISS' ? true : false)
    setHasTreatmentCourse(userSetup.hasTreatmentCourse === 'PERMISS' ? true : false)
    setHasDoctorNote(userSetup.hasDoctorNote === 'PERMISS' ? true : false)
    setHasCancelVisit(userSetup.hasCancelVisit === 'PERMISS' ? true : false)
    setHasCancelLabOrder(userSetup.hasCancelLabOrder === 'PERMISS' ? true : false)
    setHasReplyLabOrder(userSetup.hasReplyLabOrder === 'PERMISS' ? true : false)
    setHasViewIncome(userSetup.hasViewIncome === 'PERMISS' ? true : false)
    setIsViewDfAndLab(userSetup.isViewDfAndLab === '1' ? true : false)
    setAutoShowPatientProfile(userSetup.autoShowPatientProfile === '1' ? true : false)
    setIsViewDfConfirm(userSetup.isViewDfConfirm === '1' ? true : false)

    // NEW 8/6/2023
    setHasTreatmentAdd(userSetup.hasCreateTreatment === 'PERMISS' ? true : false)
    setHasTreatmentDelete(userSetup.hasDeleteTreatment === 'PERMISS' ? true : false)
    setHasTreatmentPlanAdd(userSetup.hasCreateTreatmentPlan === 'PERMISS' ? true : false)
    setHasTreatmentPlanDelete(userSetup.hasDeleteTreatmentPlan === 'PERMISS' ? true : false)
    setHasTreatmentCourseAdd(userSetup.hasCreateTreatmentCourse === 'PERMISS' ? true : false)
    setHasTreatmentCourseDelete(userSetup.hasDeleteTreatmentCourse === 'PERMISS' ? true : false)
    setHasDoctorNoteAdd(userSetup.hasCreateDoctorNote === 'PERMISS' ? true : false)
    setHasDoctorNoteDelete(userSetup.hasDeleteDoctorNote === 'PERMISS' ? true : false)

    setHasCancelTreatmentQueue(userSetup.hasCancelTreatmentQueue === 'PERMISS' ? true : false)
    setHasEditBill(userSetup.hasEditBill === 'PERMISS' ? true : false)
    setHasResetBill(userSetup.hasResetBill === 'PERMISS' ? true : false)
    setHasInsuranceCost(userSetup.hasInsuranceCost === 'PERMISS' ? true : false)
    setHasShowPhoneNumber(userSetup.hasShowPhoneNumber === 'PERMISS' ? true : false)

    setHasDiagnosis(userSetup.hasDiagnosis === 'PERMISS' ? true : false)
    setHasCreateDiagnosis(userSetup.hasCreateDiagnosis === 'PERMISS' ? true : false)
    setHasDeleteDiagnosis(userSetup.hasDeleteDiagnosis === 'PERMISS' ? true : false)

    setHasMed(userSetup?.hasMed === 'PERMISS' ? true : false)
    setHasCreateMed(userSetup?.hasCreateMed === 'PERMISS' ? true : false)
    setHasDeleteMed(userSetup?.hasDeleteMed === 'PERMISS' ? true : false)

    setHasProduct(userSetup?.hasProduct === 'PERMISS' ? true : false)
    setHasCreateProduct(userSetup?.hasCreateProduct === 'PERMISS' ? true : false)
    setHasDeleteProduct(userSetup?.hasDeleteProduct === 'PERMISS' ? true : false)

    setHasApproveIncomeAndExpenses(userSetup?.hasApproveIncomeAndExpenses === 'PERMISS' ? true : false)
    setHasApproveTheAmountChange(userSetup?.hasApproveTheAmountChange === 'PERMISS' ? true : false)
    setHasOverdueRequest(userSetup?.hasOverdueRequest === 'PERMISS' ? true : false)
  }, [loading, loadingProfile, userSetup])


  useEffect(() => {
    if (isMounted.current) {
      setHasSelectAll(isSelecAll(userSetup));
    }
  }, [
    userSetup, hasCancelBill, hasChangeTreatmentCost, hasChangeTreatmentNote, hasTreatment, hasChangePrice,
    hasChangeDiscountAdditional, hasManageAdvances, hasUnfollow, hasTreatmentPlan, hasTreatmentCourse, hasEditCourse, hasDoctorNote,
    hasCancelVisit, hasCancelLabOrder, hasReplyLabOrder, hasViewIncome, isViewDfAndLab, isViewDfConfirm,
    autoShowPatientProfile, hasTreatmentAdd, hasTreatmentDelete, hasTreatmentPlanAdd, hasTreatmentPlanDelete,
    hasTreatmentCourseAdd, hasTreatmentCourseDelete, hasDoctorNoteAdd, hasDoctorNoteDelete,
    hasCancelTreatmentQueue, hasInsuranceCost, hasEditBill, hasResetBill, hasShowPhoneNumber,
    hasDiagnosis, hasCreateDiagnosis, hasDeleteDiagnosis, hasMed, hasCreateMed, hasDeleteMed,
    hasProduct, hasCreateProduct, hasDeleteProduct, hasApproveIncomeAndExpenses, hasApproveTheAmountChange
  ]);


  useEffect(() => {
    if (!loading && isSelecAll(userSetup)) {
      selectAll()
    } else if (!loading && hasSelectAll) {
      selectAll()
    }
  }, [hasSelectAll])

  const [anchorElNorm, setAnchorElNorm] = useState<null | HTMLElement>(null)
  const openNorm = Boolean(anchorElNorm)
  const handleClickNorm = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElNorm(event.currentTarget)
  }

  const [anchorElDen, setAnchorElDen] = useState<null | HTMLElement>(null)
  const openDen = Boolean(anchorElDen)
  const handleClickDen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDen(event.currentTarget)
  }

  const isSelecAll = (data: any) => {
    const keys = ['hasResetBill', 'hasEditBill', 'hasCancelTreatmentQueue', 'hasDeleteDoctorNote', 'hasCreateDoctorNote', 'hasDeleteTreatmentCourse',
      'hasCreateTreatmentCourse', 'hasEditCourse', 'hasDeleteTreatmentPlan', 'hasCreateTreatmentPlan', 'hasDeleteTreatment', 'hasCreateTreatment', 'isViewDfAndLab', 'autoShowPatientProfile', 'isViewDfConfirm',
      'hasViewIncome', 'hasReplyLabOrder', 'hasCancelLabOrder', 'hasCancelVisit', 'hasDoctorNote', 'hasTreatmentCourse', 'hasTreatmentPlan',
      'hasChangePrice', 'hasChangeDiscountAdditional', 'hasManageAdvances', 'hasUnfollow', 'hasTreatment', 'hasChangeTreatmentNote', 'hasChangeTreatmentCost', 'hasCancelBill', 'hasInsuranceCost', 'hasShowPhoneNumber',
      'hasDiagnosis', 'hasCreateDiagnosis', 'hasDeleteDiagnosis', 'hasApproveIncomeAndExpenses', 'hasApproveTheAmountChange', 'hasOverdueRequest']

    const hasManageTreatmentCostPermission = data.hasChangeTreatmentCost === 'PERMISS'

    return !keys.find(item => data[item] === '0' || data[item] === 'UNPERMISS') && hasManageTreatmentCostPermission
  }

  const handleSelectNorm = (e: any) => {
    dispatch(editUser({ value: e.target.value, key: 'ownAppointment' }))
    setScheduleNomal(e.target.value)
    setAnchorElNorm(null)
  }

  const handleSelectDen = (e: any) => {
    dispatch(editUser({ value: e.target.value, key: 'ownSchedule' }))
    setScheduleDentist(e.target.value)
    setAnchorElDen(null)
  }

  const clearReadOnly = () => {
    setReadOnly(false)
  }

  const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enteredValue = event.target.value.trim()
    setPassword(enteredValue)
    dispatch(editUser({ value: enteredValue, key: 'password' }))
    if (props.resetErrorMessage) props.resetErrorMessage('setup')
  }
  const selectAll = () => {
    setHasCancelBill(hasSelectAll)
    setHasEditCourse(hasSelectAll)
    setHasChangeTreatmentNote(hasSelectAll)
    setHasTreatment(hasSelectAll)
    setHasChangePrice(hasSelectAll)
    setHasChangeTreatmentCost(hasSelectAll ? '1' : '0')
    setHasChangeDiscountAdditional(hasSelectAll)
    setManageTreatmentCostType(hasSelectAll ? 'DEFAULT' : '')
    setHasManageAdvances(hasSelectAll)
    setHasUnfollow(hasSelectAll)
    setHasTreatmentPlan(hasSelectAll)
    setHasTreatmentCourse(hasSelectAll)
    setHasDoctorNote(hasSelectAll)
    setHasCancelVisit(hasSelectAll)
    setHasCancelLabOrder(hasSelectAll)
    setHasReplyLabOrder(hasSelectAll)
    setHasViewIncome(hasSelectAll)
    setIsViewDfAndLab(hasSelectAll)
    setIsViewDfConfirm(hasSelectAll)
    setAutoShowPatientProfile(hasSelectAll)

    setHasTreatmentAdd(hasSelectAll)
    setHasTreatmentDelete(hasSelectAll)
    setHasTreatmentPlanAdd(hasSelectAll)
    setHasTreatmentPlanDelete(hasSelectAll)
    setHasTreatmentCourseAdd(hasSelectAll)
    setHasTreatmentCourseDelete(hasSelectAll)
    setHasDoctorNoteAdd(hasSelectAll)
    setHasDoctorNoteDelete(hasSelectAll)

    setHasCancelTreatmentQueue(hasSelectAll)
    setHasInsuranceCost(hasSelectAll)
    setHasEditBill(hasSelectAll)
    setHasResetBill(hasSelectAll)
    setHasShowPhoneNumber(hasSelectAll)

    setHasDiagnosis(hasSelectAll)
    setHasCreateDiagnosis(hasSelectAll)
    setHasDeleteDiagnosis(hasSelectAll)

    setHasMed(hasSelectAll)
    setHasCreateMed(hasSelectAll)
    setHasDeleteMed(hasSelectAll)

    setHasProduct(hasSelectAll)
    setHasCreateProduct(hasSelectAll)
    setHasDeleteProduct(hasSelectAll)

    setHasOverdueRequest(hasSelectAll)

    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasViewIncome', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasTreatment', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateTreatment', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteTreatment', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasTreatmentPlan', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateTreatmentPlan', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteTreatmentPlan', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasTreatmentCourse', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasEditCourse', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateTreatmentCourse', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteTreatmentCourse', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDoctorNote', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateDoctorNote', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteDoctorNote', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasChangePrice', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasChangeDiscountAdditional', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasManageAdvances', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasUnfollow', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasChangeTreatmentNote', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasChangeTreatmentCost', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelBill', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelVisit', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelLabOrder', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasReplyLabOrder', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelTreatmentQueue', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasInsuranceCost', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasEditBill', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasResetBill', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasShowPhoneNumber', }))
    dispatch(editUser({ value: hasSelectAll === true ? '1' : '0', key: 'isViewDfAndLab' }))
    dispatch(editUser({ value: hasSelectAll === true ? '1' : '0', key: 'isViewDfConfirm' }))
    dispatch(editUser({ value: hasSelectAll === true ? '1' : '0', key: 'autoShowPatientProfile' }))

    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDiagnosis', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateDiagnosis', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteDiagnosis', }))

    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasMed', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateMed', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteMed', }))

    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasProduct', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateProduct', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteProduct', }))

    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasApproveIncomeAndExpenses', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasApproveTheAmountChange', }))
    dispatch(editUser({ value: hasSelectAll === true ? 'PERMISS' : 'UNPERMISS', key: 'hasOverdueRequest', }))

  }

  const handleManageTreatmentCostType = (event: any) => {
    const value = event?.target?.value || ''
    setManageTreatmentCostType(value)
    dispatch(editUser({ value: 'PERMISS', key: 'hasChangeTreatmentCost' }))
    dispatch(editUser({ value, key: 'manageTreatmentCostType' }))
  }

  const handleToggleManageTreatmentCosts = (event: any) => {
    const checked = event?.target?.checked
    setHasChangeTreatmentCost(checked ? '1' : '0')

    if (checked) {
      const nextType = manageTreatmentCostType || 'DEFAULT'
      setManageTreatmentCostType(nextType)
      dispatch(editUser({ value: 'PERMISS', key: 'hasChangeTreatmentCost' }))
      dispatch(editUser({ value: nextType, key: 'manageTreatmentCostType' }))
      return
    }

    setManageTreatmentCostType('')
    dispatch(editUser({ value: 'UNPERMISS', key: 'hasChangeTreatmentCost' }))
    dispatch(editUser({ value: 'DEFAULT', key: 'manageTreatmentCostType' }))
  }

  const renderPayment = [
    {
      menuName: t('PERSONNEL.SET_MENU.DX'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasDiagnosis || false,
          onChange: (e: any) => {
            setHasDiagnosis(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDiagnosis' }))
          },
          // disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'ADD',
          checked: hasCreateDiagnosis || false,
          onChange: (e: any) => {
            setHasCreateDiagnosis(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateDiagnosis' }))
          },
          // disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'DEL',
          checked: hasDeleteDiagnosis || false,
          onChange: (e: any) => {
            setHasDeleteDiagnosis(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteDiagnosis' }))
          },
          // disabled: isPackage?.package === 'STANDARD'
        }
      ]
    },
    {
      menuName: t('PERSONNEL.SET_MENU.TX'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasTreatment || false,
          onChange: (e: any) => {
            setHasTreatment(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasTreatment' }))
          }
        },
        {
          detailName: 'ADD',
          checked: hasTreatmentAdd || false,
          onChange: (e: any) => {
            setHasTreatmentAdd(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateTreatment' }))
          }
        },
        {
          detailName: 'DEL',
          checked: hasTreatmentDelete || false,
          onChange: (e: any) => {
            setHasTreatmentDelete(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteTreatment' }))
          }
        }
      ]
    },
    {
      menuName: t('PERSONNEL.SET_MENU.TX_P'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasTreatmentPlan || false,
          onChange: (e: any) => {
            setHasTreatmentPlan(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasTreatmentPlan' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'ADD',
          checked: hasTreatmentPlanAdd || false,
          onChange: (e: any) => {
            setHasTreatmentPlanAdd(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateTreatmentPlan' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'DEL',
          checked: hasTreatmentPlanDelete || false,
          onChange: (e: any) => {
            setHasTreatmentPlanDelete(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteTreatmentPlan' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        }
      ]
    },
    {
      menuName: t('PERSONNEL.SET_MENU.TX_C'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasTreatmentCourse || false,
          onChange: (e: any) => {
            setHasTreatmentCourse(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasTreatmentCourse' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'ADD',
          checked: hasTreatmentCourseAdd || false,
          onChange: (e: any) => {
            setHasTreatmentCourseAdd(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateTreatmentCourse' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'DEL',
          checked: hasTreatmentCourseDelete || false,
          onChange: (e: any) => {
            setHasTreatmentCourseDelete(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteTreatmentCourse' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        }
      ]
    },
    {
      menuName: t('PERSONNEL.SET_MENU.MED'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasMed || false,
          onChange: (e: any) => {
            setHasMed(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasMed' }))
          }
        },
        {
          detailName: 'ADD',
          checked: hasCreateMed || false,
          onChange: (e: any) => {
            setHasCreateMed(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateMed' }))
          }
        },
        {
          detailName: 'DEL',
          checked: hasDeleteMed || false,
          onChange: (e: any) => {
            setHasDeleteMed(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteMed' }))
          }
        },

      ]
    },
    {
      menuName: t('PERSONNEL.SET_MENU.PD'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasProduct || false,
          onChange: (e: any) => {
            setHasProduct(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasProduct' }))
          }
        },
        {
          detailName: 'ADD',
          checked: hasCreateProduct || false,
          onChange: (e: any) => {
            setHasCreateProduct(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateProduct' }))
          }
        },
        {
          detailName: 'DEL',
          checked: hasDeleteProduct || false,
          onChange: (e: any) => {
            setHasDeleteProduct(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteProduct' }))
          }
        },

      ]
    },
    {
      menuName: t('PERSONNEL.SET_MENU.DOCTOR_NOTE'),
      detail: [
        {
          detailName: 'WATCH',
          checked: hasDoctorNote || false,
          onChange: (e: any) => {
            setHasDoctorNote(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDoctorNote' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'ADD',
          checked: hasDoctorNoteAdd || false,
          onChange: (e: any) => {
            setHasDoctorNoteAdd(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCreateDoctorNote' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },
        {
          detailName: 'DEL',
          checked: hasDoctorNoteDelete || false,
          onChange: (e: any) => {
            setHasDoctorNoteDelete(e.target.checked)
            dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasDeleteDoctorNote' }))
          },
          disabled: isPackage?.package === 'STANDARD'
        },

      ]
    },
  ]

  const handleDetailNameItems = (datailName: string) => {
    if (datailName === "WATCH") {
      return t('PERSONNEL.BUTTON.WATCH')
    } else if (datailName === "ADD") {
      return t('PERSONNEL.BUTTON.ADD')
    } else {
      return t('PERSONNEL.BUTTON.DELETE')
    }
  }

  const isEnableManageTreatmentCost =
    Boolean(manageTreatmentCostType) && hasChangeTreatmentCost === '1'

  return (
    <Box className="py-3 px-4">
      {!props.isEdit && (
        <Box my={1}>
          <Grid container spacing={2} mt={'6px'}>
            <Grid item xs={12} sm={4}>
              <Box onClick={clearReadOnly}>
                <TextInput
                  required
                  label={t('PERSONNEL.INPUT.PERSONNEL_USERNUMBER')}
                  value={userCode}
                  onChange={e => {
                    dispatch(editUser({ value: e.target.value, key: 'userCode' }));
                    setUserCode(e.target.value);
                    props.resetErrorMessage?.('setup');
                  }}
                  inputProps={{ readOnly }}
                  helperText={props.errorMessage.USER_CODE}
                  fullWidth
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box onClick={clearReadOnly}>
                <TextInput
                  required
                  label={t('PERSONNEL.INPUT.PERSONNEL_USERNAME')}
                  value={username}
                  onChange={e => {
                    dispatch(editUser({ value: e.target.value, key: 'username' }));
                    setUsername(e.target.value);
                    props.resetErrorMessage?.('setup');
                  }}
                  inputProps={{ readOnly }}
                  helperText={props.errorMessage.USER_NAME}
                  fullWidth
                />
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={'6px'}>
            <Grid item xs={12} sm={4}>
              <Box onClick={clearReadOnly}>
                <InputPasswordField
                  label={t('PERSONNEL.INPUT.PERSONNEL_PASSWORD')}
                  value={password}
                  onchange={handlePassword}
                  helperText={props.errorMessage.PASSWORD || props.errorMessage.MINIMUM_PASSWORD || ''}
                  inputProps={{ readOnly }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box onClick={clearReadOnly}>
                <InputPasswordField
                  label={t('PERSONNEL.INPUT.PERSONNEL_CONFIRM_PASSWORD')}
                  value={confirmPass}
                  onchange={e => {
                    dispatch(editUser({ value: e.target.value, key: 'confirmPassword' }));
                    setConfirmPass(e.target.value);
                    props.resetErrorMessage?.('setup');
                  }}
                  helperText={props.errorMessage.CONFIRM_PASSWORD || props.errorMessage.PASSWORD_NOT_MATCH || ''}
                  inputProps={{ readOnly }}
                />
              </Box>
            </Grid>
          </Grid>
          <Divider className="mx-0 my-4" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />
        </Box>
      )}

      <Grid container>
        <Grid item xs={12} sx={{ px: 2 }}>
          <Box display="flex" alignItems="center" sx={{ fontSize: 18, fontWeight: 600, }}>
            <Typography component="span">{t('PERSONNEL.MENU_PERMISSION')}</Typography>
            <Box ml={2}><ButtonInfoSimple text={t('PERSONNEL.SETTING_PERMISSION')} /></Box>
          </Box>
        </Grid>
      </Grid>

      <Grid container alignItems="center" sx={{ mt: '8px' }}>
        <Grid item xs={12} sm={3} sx={{ px: 2 }}>
          <Box sx={{ minHeight: 35, px: 2, display: 'flex', alignItems: 'center' }}>
            <Typography component="strong" sx={{ fontSize: 18, fontWeight: 500 }}>
              {t('PERSONNEL.SCHEDULE_APP')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs>
          <Box sx={{ '.MuiFormGroup-root .MuiBox-root': { width: '30%' } }}>
            <RadioButton
              name="scheduleApp"
              value={scheduleNormal}
              onChange={handleSelectNorm}
              row
              sx={{ gap: 2 }}
              options={[
                { label: t('USER.BUTTON.ALL'), value: '0' },
                { label: t('USER.BUTTON.ONLY'), value: '1' }
              ]}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container alignItems="center" sx={{ mt: '8px' }}>
        <Grid item xs={12} sm={3} sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ minHeight: 35, px: 2, display: 'flex', alignItems: 'center' }}>
            <Typography component="strong" sx={{ fontSize: 18, fontWeight: 500 }}>
              {t('PERSONNEL.DOCTOR_SCHEDULE')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs>
          <Box sx={{ '.MuiFormGroup-root .MuiBox-root': { width: '30%' } }} >
            <RadioButton
              name="scheduleDentist"
              onChange={handleSelectDen}
              sx={{ gap: 2 }}
              row
              value={scheduleDentist}
              options={[
                { label: t('USER.BUTTON.ALL'), value: '0' },
                { label: t('USER.BUTTON.ONLY'), value: '1' }
              ]}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider className="mx-0 mt-3 mb-2" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

      <Box>
        <Grid container sx={{ mt: 3 }}>
          <Grid item xs={12} sm={3} sx={{ px: '0 4px 0 12px' }}>
            <Box sx={{ minHeight: 35, px: 2, display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ m: 0, fontSize: 18, fontWeight: 500 }}>
                {t('USER.BUTTON.SELECT_ALL')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={9} sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
            <CheckboxButton
              label=" "
              checked={hasSelectAll}
              onChange={e => setHasSelectAll(e.target.checked)}
            />
          </Grid>
        </Grid>

        <Accordion defaultExpanded sx={{ width: '100%', mt: 2 }}>
          <AccordionSummary aria-controls="panel-home-content">
            <Typography variant="h6">{t('PERSONNEL.FORM.HOME')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={9} sx={{ px: 2 }}>
                <CheckboxButton
                  label={t('PERSONNEL.INPUT.INFORMATION.LIST_VIEW_ALL')}
                  checked={hasViewIncome || false}
                  onChange={e => {
                    setHasViewIncome(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasViewIncome' }))
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ width: '100%', mt: 2 }}>
          <AccordionSummary aria-controls="panel-treatment-content">
            <Typography variant="h6">{t('PERSONNEL.FORM.TREAMENT')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.SUM_DF_LAB')}
                  checked={isViewDfAndLab || false}
                  onChange={e => {
                    setIsViewDfAndLab(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? '1' : '0', key: 'isViewDfAndLab' }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.DF')}
                  checked={isViewDfConfirm || false}
                  onChange={e => {
                    setIsViewDfConfirm(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? '1' : '0', key: 'isViewDfConfirm' }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.DATA_PATIENT')}
                  checked={autoShowPatientProfile || false}
                  onChange={e => {
                    setAutoShowPatientProfile(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? '1' : '0', key: 'autoShowPatientProfile' }))
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded sx={{ width: '100%' }}>
          <AccordionSummary>
            <Typography variant="h6">{t('PERSONNEL.FORM.PAYMENT')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid
              container
              sx={{
                display: { xs: 'none', sm: 'flex' },
                justifyContent: 'flex-end',
                mt: 2
              }}
            >
              <Grid item xs={6} sx={{ display: 'flex' }}>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  {t('PERSONNEL.BUTTON.WATCH')}
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  {t('PERSONNEL.BUTTON.ADD_')}
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  {t('PERSONNEL.BUTTON.DELETE')}
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                </Box>
              </Grid>
            </Grid>
            {renderPayment.map((item: any, idx: any) => {
              const isDisabled = item.detail.every((d: any) => d?.disabled);
              return (
                <Grid
                  container
                  key={idx}
                  spacing={2}
                  sx={{
                    mt: 2,
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}
                >
                  {/* ชื่อเมนู */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: isDisabled ? '#808080' : 'inherit'
                      }}
                    >
                      {item.menuName}
                      {item.detail.every((d: any) => d.disabled !== undefined) && (
                        <Tooltip
                          title={t(
                            'ฟังก์ชันนี้สามารถ ใช้งานได้ในแพคเกจ PREMIUM และ ULTRA'
                          )}
                          placement="top-start"
                        >
                          <IconClinicDisabeld />
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  {/* Checkbox รายละเอียด */}
                  <Grid item xs={12} sm={6}>
                    <Grid container>
                      {item.detail.map((detail: any, j: any) => (
                        <Grid
                          item
                          xs={3}
                          key={j}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            {handleDetailNameItems(detail.detailName)}
                          </Box>
                          <CheckboxButton
                            label=""
                            checked={!isDisabled && detail.checked}
                            onChange={e => detail.onChange(e)}
                            disabled={detail.disabled}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
            <Divider sx={{ my: 3, borderColor: colors.lightGray }} />
            {/* การตั้งค่าเพิ่มเติม */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.EDIT_ADDITIONAL_INFORMATION')}
                  checked={hasChangeTreatmentNote}
                  onChange={e => {
                    setHasChangeTreatmentNote(e.target.checked);
                    dispatch(
                      editUser({
                        key: 'hasChangeTreatmentNote',
                        value: e.target.checked ? 'PERMISS' : 'UNPERMISS'
                      })
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.EDIT_DRUGS_AND_PRODUCTS_PRICE')}
                  checked={hasChangePrice}
                  onChange={e => {
                    setHasChangePrice(e.target.checked);
                    dispatch(
                      editUser({
                        key: 'hasChangePrice',
                        value: e.target.checked ? 'PERMISS' : 'UNPERMISS'
                      })
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.EDIT_DISCOUNTS_AND_SURCHARGES')}
                  checked={hasChangeDiscountAdditional || false}
                  onChange={e => {
                    setHasChangeDiscountAdditional(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasChangeDiscountAdditional' }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.EDIT_COURSE_TREATMENT')}
                  checked={hasEditCourse}
                  onChange={e => {
                    setHasEditCourse(e.target.checked);
                    dispatch(
                      editUser({
                        key: 'hasEditCourse',
                        value: e.target.checked ? 'PERMISS' : 'UNPERMISS'
                      })
                    );
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded sx={{ width: '100%', mt: 2 }}>
          <AccordionSummary aria-controls="panel-payment-content">
            <Typography variant="h6">{t('PERSONNEL.FORM.PAYMENT')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'flex-end', alignItems: 'center', mt: 1, pt: 2 }}>
              <Grid item sm={6} sx={{ textAlign: 'center' }}>{''}</Grid>
              <Grid item sm={2} sx={{ textAlign: 'start', pl: 7 }}>{t('PERSONNEL.BUTTON.WATCH')}</Grid>
              <Grid item sm={1} sx={{ textAlign: 'center' }}>{t('PERSONNEL.BUTTON.ADD_')}</Grid>
              <Grid item sm={3} sx={{ textAlign: 'start', pl: 11 }}>{t('PERSONNEL.BUTTON.DELETE')}</Grid>
            </Grid>
            <Grid container spacing={1}>
              {renderPayment.map((item, idx) => {
                const isDisabled = item.detail.every((info: any) => info?.disabled)
                return (
                  <Grid item xs={12} sm={12} key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Grid item xs={12} sm={9}>
                      <Box sx={{ color: isDisabled ? '#808080' : 'inherit', display: 'flex', alignItems: 'center' }}>
                        {item.menuName}
                        {item.detail.every((info: any) => info?.disabled !== undefined) && (
                          <Tooltip
                            title={t('ฟังก์ชันนี้สามารถ ใช้งานได้ในแพคเกจ PREMIUM และ ULTRA')}
                            placement="top-start"
                          >
                            <IconClinicDisabeld />
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} container justifyContent="center" alignItems="center">
                      {item.detail.map((detail: any, ix: any) => (
                        <Grid item xs={3} key={ix} sx={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            {handleDetailNameItems(detail.detailName)}
                          </Box>
                          <CheckboxButton
                            checked={!isDisabled && detail.checked}
                            disabled={detail.disabled}
                            onChange={e => detail.onChange(e)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box mb={2}>
        <Accordion defaultExpanded sx={{ width: '100%' }}>
          <AccordionSummary aria-controls="panel2d-content">
            <Typography variant="h6" sx={{ m: 0 }}>
              {t('PERSONNEL.FORM.OTHER_PRIVILEGES')}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2} sx={{ mt: '16px' }}>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.GUARANTEE_COST_SHIFTS')}
                  checked={hasInsuranceCost || false}
                  onChange={e => {
                    setHasInsuranceCost(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasInsuranceCost' }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.EDIT_RECEIPT')}
                  checked={hasEditBill || false}
                  onChange={e => {
                    setHasEditBill(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasEditBill' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.CANCEL_VISIT')}
                  checked={hasCancelVisit || false}
                  onChange={e => {
                    setHasCancelVisit(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelVisit' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ minHeight: 40, display: 'flex', alignItems: 'center' }}>
                    <CheckboxButton
                      label={t('PERSONNEL.FORM.MANAGE_TREATMENT_COSTS')}
                      checked={hasChangeTreatmentCost === '1'}
                      onChange={handleToggleManageTreatmentCosts}
                    />
                  </Box>
                  <Box sx={{ pl: 4, opacity: isEnableManageTreatmentCost ? 1 : 0.6 }}>
                    <RadioButton
                      name="manageTreatmentCostType"
                      row
                      value={manageTreatmentCostType}
                      onChange={handleManageTreatmentCostType}
                      options={[
                        { label: t('PERSONNEL.FORM.MANAGE_TREATMENT_COSTS_DEFAULT'), value: 'DEFAULT', disabled: !isEnableManageTreatmentCost },
                        { label: t('PERSONNEL.FORM.MANAGE_TREATMENT_COSTS_DCC'), value: 'DCC', disabled: !isEnableManageTreatmentCost }
                      ]}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.RESET_RECEIPT')}
                  checked={hasResetBill || false}
                  onChange={e => {
                    setHasResetBill(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasResetBill' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.CANCEL_TREATMENT_QUEUE')}
                  checked={hasCancelTreatmentQueue || false}
                  onChange={e => {
                    setHasCancelTreatmentQueue(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelTreatmentQueue' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.SHOW_PATIENT_PHONE_NUMBER')}
                  checked={hasShowPhoneNumber || false}
                  onChange={e => {
                    setHasShowPhoneNumber(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasShowPhoneNumber' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.CANCEL_RECEIPT')}
                  checked={hasCancelBill || false}
                  onChange={e => {
                    setHasCancelBill(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelBill' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '& .Mui-disabled': { opacity: 1 },
                  }}
                >
                  <CheckboxButton
                    label={t('PERSONNEL.FORM.CANCEL_LAB_FORM')}
                    checked={isPackage?.package === 'STANDARD' ? false : hasCancelLabOrder || false}
                    onChange={e => {
                      setHasCancelLabOrder(e.target.checked)
                      dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasCancelLabOrder' }))
                    }}
                    disabled={isPackage?.package === 'STANDARD'}
                  />
                  <Tooltip
                    title={t('ฟังก์ชันนี้สามารถ ใช้งานได้ในแพคเกจ PREMIUM และ ULTRA')}
                    placement="top-start"
                  >
                    <Box component="span" sx={{ ml: 1 }}>
                      <IconClinicDisabeld />
                    </Box>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.APPROVE_INCOME_EXPENSES')}
                  checked={hasApproveIncomeAndExpenses || false}
                  onChange={e => {
                    setHasApproveIncomeAndExpenses(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasApproveIncomeAndExpenses' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.APPROVE_AMOUNT_CHANGE')}
                  checked={hasApproveTheAmountChange || false}
                  onChange={e => {
                    setHasApproveTheAmountChange(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasApproveTheAmountChange' }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.EDIT_MANAGE_ADVANCE_PAYMENTS')}
                  checked={hasManageAdvances || false}
                  onChange={e => {
                    setHasManageAdvances(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasManageAdvances' }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.UN_FOLLOW_UP')}
                  checked={hasUnfollow || false}
                  onChange={e => {
                    setHasUnfollow(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasUnfollow' }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CheckboxButton
                  label={t('PERSONNEL.FORM.OVERDUE_REQUEST')}
                  checked={hasOverdueRequest || false}
                  onChange={e => {
                    setHasOverdueRequest(e.target.checked)
                    dispatch(editUser({ value: e.target.checked ? 'PERMISS' : 'UNPERMISS', key: 'hasOverdueRequest' }))
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Loading show={loading} type='fullLoading' />
    </Box >
  )
}
